import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import type { ReactNode } from 'react';
import type { AuthUser, Role } from '@/types';
import { supabase, isSupabaseConfigured } from '@/lib/supabase';
import { mockAuthUsers } from '@/mocks/auth';

interface AuthContextValue {
  user: AuthUser | null;
  isLoading: boolean;
  signInWithOtp: (email: string) => Promise<{ success: boolean; error?: string }>;
  signInWithPassword: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
  signOut: () => Promise<void>;
  switchUser: (role: Role) => void;
  previewRole: Role | null;
  setPreviewRole: (role: Role | null) => void;
  effectiveRole: Role | null;
  effectivePartnerId: string | null;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

const MOCK_KEY = 'ln_mock_auth_user';
const PREVIEW_KEY = 'ln_preview_role';

function getStoredMock(): AuthUser | null {
  const raw = localStorage.getItem(MOCK_KEY);
  if (!raw) return null;
  try { return JSON.parse(raw) as AuthUser; } catch { return null; }
}

async function fetchProfile(userId: string): Promise<AuthUser | null> {
  try {
    const { data, error } = await supabase
      .from('profiles')
      .select('id, role, partner_id, full_name')
      .eq('id', userId)
      .maybeSingle();

    if (error || !data) return null;

    const { data: userData } = await supabase.auth.getUser();
    const email = userData?.user?.email || '';

    return {
      id: data.id,
      email,
      role: data.role as Role,
      partner_id: data.partner_id || null,
      full_name: data.full_name || null,
    };
  } catch {
    return null;
  }
}

async function resolveUserProfile(userId: string, email: string): Promise<AuthUser | null> {
  const profile = await Promise.race([
    fetchProfile(userId),
    new Promise<null>(resolve => setTimeout(() => resolve(null), 3000)),
  ]);
  if (profile) return profile;
  // Profile fetch failed or timed out — fall back to mock user by email
  return mockAuthUsers.find(u => u.email === email) || null;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [useMock, setUseMock] = useState(false);
  const [previewRole, setPreviewRoleState] = useState<Role | null>(() => {
    const raw = localStorage.getItem(PREVIEW_KEY);
    if (!raw) return null;
    try {
      const parsed = JSON.parse(raw) as Role;
      const validRoles: Role[] = ['operator', 'account_manager', 'partner_principal', 'partner_staff', 'read_only'];
      return validRoles.includes(parsed) ? parsed : null;
    } catch {
      return null;
    }
  });

  // Try real Supabase auth first
  useEffect(() => {
    let mounted = true;

    async function init() {
      setIsLoading(true);

      // Check if we have a mock user stored
      const mock = getStoredMock();
      if (mock) {
        setUser(mock);
        setUseMock(true);
        setIsLoading(false);
        return;
      }

      // Only try real auth if Supabase is actually configured
      if (!isSupabaseConfigured) {
        if (mounted) setIsLoading(false);
        return;
      }

      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.user) {
          const resolved = await resolveUserProfile(session.user.id, session.user.email || '');
          if (mounted) {
            setUser(resolved);
            setIsLoading(false);
          }
        } else {
          if (mounted) setIsLoading(false);
        }
      } catch {
        if (mounted) setIsLoading(false);
      }
    }

    init();

    let listener: { subscription: { unsubscribe: () => void } } | null = null;
    if (isSupabaseConfigured) {
      const { data } = supabase.auth.onAuthStateChange(async (event, session) => {
        if (event === 'SIGNED_OUT') {
          if (mounted && !useMock) setUser(null);
        } else if (session?.user) {
          // USER_UPDATED / TOKEN_REFRESHED: re-resolve but never clear an existing session
          const resolved = await resolveUserProfile(session.user.id, session.user.email || '');
          if (mounted && resolved) setUser(resolved);
        }
      });
      listener = data;
    }

    return () => {
      mounted = false;
      listener?.subscription.unsubscribe();
    };
  }, []);

  const signInWithOtp = useCallback(async (email: string) => {
    setIsLoading(true);

    // Real Supabase flow first
    if (isSupabaseConfigured) {
      try {
        const result = await supabase.auth.signInWithOtp({ email });
        if (result.error) throw result.error;
        setIsLoading(false);
        return { success: true };
      } catch (err: any) {
        // If Supabase OTP fails, fall back to mock for demo / dev accounts
        const found = mockAuthUsers.find(u => u.email === email);
        if (found) {
          setUser(found);
          setUseMock(true);
          localStorage.setItem(MOCK_KEY, JSON.stringify(found));
          setIsLoading(false);
          return { success: true };
        }
        setIsLoading(false);
        return { success: false, error: err?.message || 'Failed to send login link' };
      }
    }

    // Fallback to mock mode if Supabase is not configured
    const found = mockAuthUsers.find(u => u.email === email);
    if (found) {
      setUser(found);
      setUseMock(true);
      localStorage.setItem(MOCK_KEY, JSON.stringify(found));
      setIsLoading(false);
      return { success: true };
    }
    setIsLoading(false);
    return { success: false, error: 'No account found.' };
  }, []);

  const signInWithPassword = useCallback(async (email: string, password: string) => {
    setIsLoading(true);

    // Real Supabase flow first
    if (isSupabaseConfigured) {
      let authResult: Awaited<ReturnType<typeof supabase.auth.signInWithPassword>>;
      try {
        authResult = await supabase.auth.signInWithPassword({ email, password });
        if (authResult.error) throw authResult.error;
      } catch (err: any) {
        // Auth itself failed — fall back to mock for demo / dev accounts
        const found = mockAuthUsers.find(u => u.email === email);
        if (found) {
          setUser(found);
          setUseMock(true);
          localStorage.setItem(MOCK_KEY, JSON.stringify(found));
          setIsLoading(false);
          return { success: true };
        }
        setIsLoading(false);
        return { success: false, error: err?.message || 'Invalid credentials' };
      }

      // Auth succeeded — resolve profile with mock fallback on failure/timeout
      if (authResult.data?.user) {
        const resolved = await resolveUserProfile(
          authResult.data.user.id,
          authResult.data.user.email || email,
        );
        setUser(resolved);
        setUseMock(false);
        localStorage.removeItem(MOCK_KEY);
      }
      setIsLoading(false);
      return { success: true };
    }

    // Fallback to mock mode if Supabase is not configured
    const found = mockAuthUsers.find(u => u.email === email);
    if (found) {
      setUser(found);
      setUseMock(true);
      localStorage.setItem(MOCK_KEY, JSON.stringify(found));
      setIsLoading(false);
      return { success: true };
    }
    setIsLoading(false);
    return { success: false, error: 'Invalid credentials or account not found.' };
  }, []);

  const signOut = useCallback(async () => {
    if (isSupabaseConfigured) {
      try {
        await supabase.auth.signOut();
      } catch {
        // Ignore errors on signout
      }
    }
    setUser(null);
    setUseMock(false);
    localStorage.removeItem(MOCK_KEY);
  }, []);

  const setPreviewRole = useCallback((role: Role | null) => {
    setPreviewRoleState(role);
    if (role) {
      localStorage.setItem(PREVIEW_KEY, JSON.stringify(role));
    } else {
      localStorage.removeItem(PREVIEW_KEY);
    }
  }, []);

  const switchUser = useCallback((role: Role) => {
    const found = mockAuthUsers.find(u => u.role === role);
    if (found) {
      setUser(found);
      setUseMock(true);
      localStorage.setItem(MOCK_KEY, JSON.stringify(found));
    }
  }, []);

  const effectiveRole = previewRole || user?.role || null;

  const effectivePartnerId = useMemo(() => {
    if (user?.partner_id) return user.partner_id;
    if (previewRole === 'partner_principal' || previewRole === 'partner_staff') {
      return 'partner-003';
    }
    return null;
  }, [user, previewRole]);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        signInWithOtp,
        signInWithPassword,
        signOut,
        switchUser,
        previewRole,
        setPreviewRole,
        effectiveRole,
        effectivePartnerId,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
